import React, { Activity, useMemo, type FC } from 'react';
import type { TextStyle, ViewStyle } from 'react-native';

import { Stack } from '@/components/layout';
import { Select } from '@/components/ui/select';
import type { SelectOption } from '@/components/ui/select/types';
import { Text, TextProps } from '@/components/ui/text';
import type { TextInputStatus } from '@/components/ui/text-input/types';
import { createStyles } from '@/hooks';
import { colors } from '@/theme/colors';
import { TypographyToken } from '@/theme/typography';

export type FormSelectInputProps = {
  /**
   * Optional label displayed above the trigger.
   * If omitted, no label row is rendered.
   */
  label?: string;
  required?: boolean;
  disabled?: boolean;

  placeholder?: string;

  options: SelectOption[];
  value?: string | null;
  defaultValue?: string | null;
  onValueChange?: (value: string) => void;

  /**
   * Close dropdown after selecting an item (default: true)
   */
  closeOnSelect?: boolean;
  status?: Exclude<TextInputStatus, 'disabled'>;
  helperText?: string;

  containerStyle?: ViewStyle | ViewStyle[];
  triggerStyle?: ViewStyle | ViewStyle[];
  /** Extra style applied on top of the default trigger look while disabled. */
  disableStyle?: ViewStyle | ViewStyle[];
  /**
   * If provided, overrides label style.
   * (Useful when you need slightly different spacing.)
   */
  labelStyle?: TextStyle | TextStyle[];
  labelProps?: Omit<TextProps, 'children' | 'style'>;
  statusBarTranslucent?: boolean;
  errorMessage?: string;
  /**
   * Uppercase the label text. Defaults to `true` (current behaviour).
   * Set `false` to render the label exactly as passed (e.g. to match Figma).
   */
  uppercaseLabel?: boolean;
  /** Override the label typography variant (default `Font Inter/Subtitle/Regular 11px`). */
  labelVariant?: TypographyToken;
  /** Extra style merged into the required asterisk. */
  requiredMarkStyle?: TextStyle | TextStyle[];
  /** Gap (px) between the label row and the trigger. Defaults to `8`. */
  labelGap?: number;
  /** Override the trigger value/placeholder typography variant. */
  triggerVariant?: TypographyToken;
  /** Override the trigger value text + chevron color. */
  triggerTextColor?: string;
  /**
   * How the options are presented when the trigger is pressed:
   * - `'dropdown'` (default): anchored dropdown next to the trigger.
   * - `'sheet'`: a bottom-sheet wheel picker with an `X / title / ✓` header (title = `label`).
   */
  presentation?: 'dropdown' | 'sheet';
  /**
   * Extra style merged into the phone bottom-sheet container when `presentation='sheet'`
   * (e.g. override the corner radius). Ignored for the `'dropdown'` presentation.
   */
  sheetStyle?: ViewStyle | ViewStyle[];
};

export const FormSelectInput: FC<FormSelectInputProps> = ({
  label,
  required = true,
  disabled = false,
  placeholder,
  options,
  value,
  defaultValue,
  onValueChange,
  closeOnSelect = true,
  status = 'default',
  helperText,
  containerStyle,
  triggerStyle,
  disableStyle,
  labelStyle,
  labelProps,
  statusBarTranslucent = false,
  errorMessage,
  uppercaseLabel = true,
  labelVariant = 'Font Inter/Subtitle/Regular 11px',
  requiredMarkStyle,
  labelGap = 8,
  triggerVariant,
  triggerTextColor,
  presentation = 'dropdown',
  sheetStyle,
}) => {
  const displayLabel = useMemo(() => {
    const text = String(label ?? '');
    return uppercaseLabel ? text.toUpperCase() : text;
  }, [label, uppercaseLabel]);
  const resolvedPlaceholder = placeholder ?? label ?? '';

  const styles = useStyles();

  return (
    <Stack direction="column" gap={labelGap} width="100%" style={containerStyle}>
      {label ? (
        <Stack direction="row" align="center" justify="flex-start">
          <Text variant={labelVariant} style={[styles.label, labelStyle]} {...labelProps}>
            {displayLabel}
          </Text>
          {required && (
            <Text variant={labelVariant} style={[styles.requiredMark, requiredMarkStyle]}>
              *
            </Text>
          )}
        </Stack>
      ) : null}

      <Select
        options={options}
        value={value}
        defaultValue={defaultValue}
        onValueChange={onValueChange}
        placeholder={resolvedPlaceholder}
        disabled={disabled}
        status={status}
        helperText={helperText}
        closeOnSelect={closeOnSelect}>
        <Select.Trigger
          style={triggerStyle}
          disableStyle={disableStyle}
          hideIcon={false}
          labelVariant={triggerVariant}
          textColor={triggerTextColor}
        />
        {presentation === 'sheet' ? (
          <Select.SheetContent title={label} sheetStyle={sheetStyle} />
        ) : (
          <Select.Content maxHeight={500} statusBarTranslucent={statusBarTranslucent} />
        )}
      </Select>

      <Activity mode={errorMessage ? 'visible' : 'hidden'}>
        <Text
          variant="Font Inter/Subtitle/Regular 11px"
          style={[styles.helperText, { color: colors.Error }]}>
          {errorMessage}
        </Text>
      </Activity>
    </Stack>
  );
};

const useStyles = createStyles(() => ({
  helperText: {
    marginTop: -2,
  },
  label: {
    color: colors['Labels/Primary'],
  },
  requiredMark: {
    marginLeft: 4,
    color: colors.Red,
  },
}));

export default FormSelectInput;
